import React, { useContext, useState, useEffect } from "react";
import PageLayout from "../../../layouts/PageLayout";
import { SidebarContext } from "../../../context/Sidebar";
import { Container, Row, Col, Button, Card, Spinner, Form, Badge } from "react-bootstrap";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import RemindersTable from "../../../components/Admin/RemindersTable";
import { FaSliders, FaClock, FaLayerGroup, FaUser, FaPaw, FaMagnifyingGlass, FaRotateLeft, FaBell, FaFilter } from "react-icons/fa6";

const ReminderPage = () => {
    const translator = useContext(TranslatorContext);
    const translate = translator?.translate || ((text) => text);
    const { setSidebar } = useContext(SidebarContext);
    const [reminders, setReminders] = useState([]);
    const [totalPending, setTotalPending] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const Authtoken = localStorage.getItem("authToken");

    const role = localStorage.getItem("role") || "";
    const isAdmin = role === "Admin";


    // New frontend filter state: "dues" (Default) or "all"
    const [completionFilter, setCompletionFilter] = useState("dues");

    const [filters, setFilters] = useState({
        type: "all",
        client_name: "",
        phone: "",
        pet_name: "",
        date_filter: "today", // Defaulted to today
        start_date: "",
        end_date: ""
    });

    const [debouncedFilters, setDebouncedFilters] = useState(filters);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedFilters(filters);
        }, 400);
        return () => clearTimeout(handler);
    }, [filters]);

    useEffect(() => {
        fetchReminders();
    }, [
        debouncedFilters.type,
        debouncedFilters.client_name,
        debouncedFilters.phone,
        debouncedFilters.pet_name,
        debouncedFilters.date_filter,
        debouncedFilters.start_date,
        debouncedFilters.end_date
    ]);

    const fetchReminders = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {};
            const token = localStorage.getItem("authToken");

            if (debouncedFilters.date_filter) params.date_filter = debouncedFilters.date_filter;

            if (debouncedFilters.date_filter === "custom") {
                if (debouncedFilters.start_date) params.start_date = debouncedFilters.start_date;
                if (debouncedFilters.end_date) params.end_date = debouncedFilters.end_date;
            }

            if (debouncedFilters.type !== "all") params.type = debouncedFilters.type;
            if (debouncedFilters.client_name) params.client_name = debouncedFilters.client_name;
            if (debouncedFilters.phone) params.phone = debouncedFilters.phone;
            if (debouncedFilters.pet_name) params.pet_name = debouncedFilters.pet_name;

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/reminders`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            if (response.data) {
                const data = response.data;
                let allReminders = [];

                if (data.vaccine_reminders) {
                    allReminders = allReminders.concat(
                        data.vaccine_reminders.map(item => ({
                            ...item,
                            type: 'vaccine',
                            type_label: 'Vaccine',
                            reminder_date: item.vaccine_date,
                            procedure_name: item.vaccine_name,
                            items: item.items || []
                        }))
                    );
                }

                if (data.deworming_reminders) {
                    allReminders = allReminders.concat(
                        data.deworming_reminders.map(item => ({
                            ...item,
                            type: 'deworming',
                            type_label: 'Deworming',
                            reminder_date: item.deworming_date,
                            procedure_name: item.deworming_name,
                            items: item.items || []
                        }))
                    );
                }

                setTotalPending(data.total_pending || allReminders.length);
                setReminders(allReminders);
            }
        } catch (error) {
            const message = error.response?.data?.errors
                ? Object.values(error.response.data.errors).flat().join(', ')
                : error.response?.data?.message || "Failed to load reminders";
            setError(message);
            setReminders([]);
            setTotalPending(0);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleDateFilterChange = (e) => {
        const value = e.target.value;
        if (value === "custom") {
            const today = new Date().toISOString().split('T')[0];
            setFilters(prev => ({ ...prev, date_filter: value, start_date: today, end_date: today }));
        } else {
            setFilters(prev => ({ ...prev, date_filter: value, start_date: "", end_date: "" }));
        }
    };

    const resetFilters = () => {
        setFilters({
            type: "all",
            client_name: "",
            phone: "",
            pet_name: "",
            date_filter: "today",
            start_date: "",
            end_date: ""
        });
        setCompletionFilter("dues"); // Reset frontend completion status filter back to Dues
        setError(null);
    };

    const dateOptions = [
        { value: "", label: "All Dates" },
        { value: "today", label: "Today" },
        { value: "this_month", label: "This Month" },
        { value: "next_3_days", label: "Next 3 Days" },
        { value: "next_7_days", label: "Next 7 Days" },
        { value: "custom", label: "Custom Range" },
    ];

    const typeOptions = [
        { value: "all", label: "All Types" },
        { value: "vaccine", label: "Vaccines" },
        { value: "deworming", label: "Deworming" },
    ];

    return (
        <PageLayout>
            <Container fluid className="py-4 px-xl-4" style={{ backgroundColor: "#f8f9fa" }}>
                {/* Dashboard Header */}
                <Row className="mb-4 align-items-center justify-content-between g-3">
                    <Col md={7}>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h2 className="text-dark fw-bold mb-0" style={{ fontSize: "1.75rem", letterSpacing: "-0.5px" }}>
                                {translate("Reminders Dashboard")}
                            </h2>
                            <Badge bg="warning" text="dark" className="px-2.5 py-1.5 rounded-pill fw-semibold d-inline-flex align-items-center gap-1 small shadow-sm">
                                <FaBell size={11} className="opacity-75" />
                                {totalPending} {translate("Pending")}
                            </Badge>
                        </div>
                        <p className="text-muted small mb-0 mt-1">Monitor, search, and track automated patient healthcare notifications</p>
                    </Col>
                    <Col xs="auto" md={5} className="d-flex justify-content-md-end">
                        <Button
                            variant="white"
                            className="bg-white text-secondary fw-semibold px-3 border rounded-3 shadow-sm d-flex align-items-center gap-2 btn-sm"
                            style={{ height: "38px" }}
                            onClick={resetFilters}
                        >
                            <FaRotateLeft size={13} className="text-muted" /> {translate("Reset Filters")}
                        </Button>
                    </Col>
                </Row>

                {/* Error Banner */}
                {error && (
                    <div className="alert alert-danger rounded-3 border-0 shadow-sm mb-4 d-flex align-items-center justify-content-between" role="alert">
                        <div><strong className="me-2">Error:</strong>{error}</div>
                        <button type="button" className="btn-close ms-3" onClick={() => setError(null)}></button>
                    </div>
                )}

                {/* Search Criteria Card */}
                <Card className="border-0 shadow-sm rounded-4 mb-4 bg-white">
                    <Card.Body className="p-4">
                        <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                            <FaSliders className="text-primary opacity-75" size={14} />
                            <h6 className="fw-bold text-secondary mb-0 small" style={{ letterSpacing: "0.5px" }}>
                                SEARCH CRITERIA
                            </h6>
                        </div>

                        <Row className="g-3">
                            {/* NEW: Completion Status Filter (Dues vs All) */}
                            <Col xl={3} lg={4} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-secondary text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>
                                        <FaFilter size={11} className="me-1 text-muted" /> Status View
                                    </Form.Label>
                                    <Form.Select
                                        value={completionFilter}
                                        onChange={(e) => setCompletionFilter(e.target.value)}
                                        className="bg-white border rounded-3 shadow-none text-dark"
                                        style={{ height: "40px", fontSize: "0.9rem", fontWeight: "600" }}
                                    >
                                        <option value="dues">Dues Only</option>
                                        <option value="all">All Records</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col xl={3} lg={4} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-secondary text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>
                                        <FaClock size={11} className="me-1 text-muted" /> Date Filter
                                    </Form.Label>
                                    <Form.Select
                                        name="date_filter"
                                        value={filters.date_filter}
                                        onChange={handleDateFilterChange}
                                        className="bg-white border rounded-3 shadow-none text-dark"
                                        style={{ height: "40px", fontSize: "0.9rem" }}
                                    >
                                        {dateOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col xl={3} lg={4} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-secondary text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>
                                        <FaLayerGroup size={11} className="me-1 text-muted" /> Procedure Type
                                    </Form.Label>
                                    <Form.Select
                                        name="type"
                                        value={filters.type}
                                        onChange={handleChange}
                                        className="bg-white border rounded-3 shadow-none text-dark"
                                        style={{ height: "40px", fontSize: "0.9rem" }}
                                    >
                                        {typeOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            {filters.date_filter === "custom" && (
                                <>
                                    <Col xl={3} lg={4} md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold text-secondary text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Start Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="start_date"
                                                value={filters.start_date}
                                                onChange={handleChange}
                                                className="bg-white border rounded-3 shadow-none text-dark"
                                                style={{ height: "40px", fontSize: "0.9rem" }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xl={3} lg={4} md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold text-secondary text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>End Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="end_date"
                                                value={filters.end_date}
                                                onChange={handleChange}
                                                className="bg-white border rounded-3 shadow-none text-dark"
                                                style={{ height: "40px", fontSize: "0.9rem" }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </>
                            )}

                            <Col xl={3} lg={4} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-secondary text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>
                                        <FaUser size={11} className="me-1 text-muted" /> Client Name
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="client_name"
                                        placeholder="Search by client..."
                                        value={filters.client_name}
                                        onChange={handleChange}
                                        className="bg-white border rounded-3 shadow-none text-dark"
                                        style={{ height: "40px", fontSize: "0.9rem" }}
                                    />
                                </Form.Group>
                            </Col>

                            <Col xl={3} lg={4} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-secondary text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>
                                        <FaMagnifyingGlass size={11} className="me-1 text-muted" /> Contact Phone
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        placeholder="Search by phone..."
                                        value={filters.phone}
                                        onChange={handleChange}
                                        className="bg-white border rounded-3 shadow-none text-dark"
                                        style={{ height: "40px", fontSize: "0.9rem" }}
                                    />
                                </Form.Group>
                            </Col>

                            <Col xl={3} lg={4} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-secondary text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>
                                        <FaPaw size={11} className="me-1 text-muted" /> Pet Name
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="pet_name"
                                        placeholder="Search by pet name..."
                                        value={filters.pet_name}
                                        onChange={handleChange}
                                        className="bg-white border rounded-3 shadow-none text-dark"
                                        style={{ height: "40px", fontSize: "0.9rem" }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Table Card */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "250px" }}>
                                <Spinner animation="border" variant="primary" size="md" className="mb-2 opacity-75" />
                                <span className="text-muted small fw-medium">{translate("Syncing records...")}</span>
                            </div>
                        ) : (
                            <RemindersTable
                                reminders={reminders}
                                AuthToken={Authtoken}
                                onRefresh={fetchReminders}
                                completionFilter={completionFilter}
                            />
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </PageLayout>
    );
};

export default ReminderPage;